import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import { Loader2, PlusCircle, X } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '../../ui/dialog';
import { Textarea } from '../../ui/textarea';
import Input from '../../ui/input';
import { Alert, AlertDescription } from '../../ui/alert';
import useQuestionDialog from '../../../hooks/useQuestionDialog';
import { TagsDisplay } from '../../form';
import {
  generateAISolution,
  generateFollowUpResponse,
  moderateQuestion,
  findSimilarQuestions,
} from '../../../services/llmService';
import { PopulatedDatabaseQuestion } from '../../../types/types';

/**
 * AskQuestionButton component that renders a button that opens a dialog
 * for asking a new question.
 */
const AskQuestionButton = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [hasInteractedWithTags, setHasInteractedWithTags] = useState(false);
  const {
    title,
    setTitle,
    text,
    setText,
    tags,
    addTag,
    removeTag,
    titleErr,
    textErr,
    tagErr,
    postQuestion,
    isSubmitting,
    resetForm,
    lastQuestionId,
  } = useQuestionDialog();
  const [aiSolution, setAiSolution] = useState<string>('');
  const [isGeneratingSolution, setIsGeneratingSolution] = useState(false);
  const [solutionError, setSolutionError] = useState<string>('');
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [followUpResponse, setFollowUpResponse] = useState<string | null>(null);
  const [isGeneratingFollowUp, setIsGeneratingFollowUp] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [moderationResponse, setModerationResponse] = useState<string>('');
  const [isModerating, setIsModerating] = useState(false);
  const [similarQuestion, setSimilarQuestion] = useState<PopulatedDatabaseQuestion | null>(null);
  const [similarityScore, setSimilarityScore] = useState<number>(0);
  const [isCheckingSimilarity, setIsCheckingSimilarity] = useState(false);
  const [moderationPassed, setModerationPassed] = useState(false);
  const [showModerationDialog, setShowModerationDialog] = useState(false);

  // Additional validation checks
  const [customErrors, setCustomErrors] = useState({
    title: '',
    text: '',
    tags: '',
  });

  // Track whether validation errors should be displayed
  const [showValidation, setShowValidation] = useState(false);

  // Validates character/word counts and tags
  const validateContentLength = React.useCallback(() => {
    const errors = { title: '', text: '', tags: '' };
    let isValid = true;

    if (title && title.length < 10) {
      errors.title = 'Title must be at least 10 characters';
      isValid = false;
    } else if (title && title.length > 100) {
      errors.title = 'Title exceeds maximum of 100 characters';
      isValid = false;
    }

    // Text validation: min 30 chars
    if (text && text.length < 30) {
      errors.text = 'Question details must be at least 30 characters';
      isValid = false;
    }

    // Tags validation - only show when user is interacting with tags field or on form submission
    if (tags && tags.length < 1) {
      errors.tags = 'Add at least one tag to categorize your question';
      isValid = false;
    }

    // Only set errors if we're showing validation
    if (showValidation) {
      setCustomErrors(errors);
    }

    return isValid;
  }, [title, text, tags, showValidation]);

  const handleModerateAndSubmit = async () => {
    setShowValidation(true);

    if (!title || !text) {
      return;
    }

    const isLengthValid = validateContentLength();

    if (!isLengthValid) {
      return;
    }

    setIsModerating(true);
    setModerationResponse('');
    setShowModerationDialog(false);

    try {
      const combinedText = `Title: ${title}\n\nQuestion Details: ${text}`;

      // Check for similar questions before moderating
      setIsCheckingSimilarity(true);
      try {
        const { similarQuestion: foundQuestion, averageSimilarity } =
          await findSimilarQuestions(combinedText);
        setSimilarQuestion(foundQuestion);
        setSimilarityScore(averageSimilarity);
      } catch (error) {
        setSolutionError(`Error checking similar questions: ${error}`);
      } finally {
        setIsCheckingSimilarity(false);
      }

      // Continue with moderation
      const result = await moderateQuestion(combinedText);

      setModerationPassed(result.success);
      setModerationResponse(result.message || '');

      setTimeout(() => {
        setShowModerationDialog(true);
      }, 100);

      if (result.success) {
        const success = await postQuestion();
        if (success) {
          setOpen(false);
          setShowModerationDialog(false);
          if (lastQuestionId) {
            navigate(`/question/${lastQuestionId}`);
          }
        }
      }
    } catch (error) {
      setModerationResponse('Failed to moderate content. Please try again.');
      setModerationPassed(false);
      setTimeout(() => {
        setShowModerationDialog(true);
      }, 100);
    } finally {
      setIsModerating(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
      setInputValue('');
      setModerationPassed(false);
      setModerationResponse('');
      setCustomErrors({ title: '', text: '', tags: '' });
      setShowValidation(false);
      setHasInteractedWithTags(false);
      setSimilarQuestion(null);
      setSimilarityScore(0);
      setAiSolution('');
      setFollowUpResponse(null);
    }
    setOpen(newOpen);
  };

  const handleTagInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setInputValue(value);
    setHasInteractedWithTags(true);
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue.trim());
      setInputValue('');
      setHasInteractedWithTags(true);
    }
  };

  const handleGenerateAISolution = async () => {
    if (!text) {
      setSolutionError('Please provide question details first');
      return;
    }

    setIsGeneratingSolution(true);
    setSolutionError('');
    try {
      const solution = await generateAISolution(text);
      setAiSolution(solution);
    } catch (error) {
      setSolutionError('Failed to generate AI solution. Please try again.');
    } finally {
      setIsGeneratingSolution(false);
    }
  };

  const handleFollowUp = async () => {
    if (!aiSolution) {
      setSolutionError('Please generate an AI solution first');
      return;
    }
    if (!followUpQuestion.trim()) {
      setSolutionError('Please enter a follow-up question');
      return;
    }

    setIsGeneratingFollowUp(true);
    try {
      const response = await generateFollowUpResponse(title, aiSolution, followUpQuestion);
      setFollowUpResponse(response);
      setFollowUpOpen(false);
      setFollowUpQuestion('');
    } catch (err) {
      setSolutionError('Failed to generate follow-up response');
    } finally {
      setIsGeneratingFollowUp(false);
    }
  };

  React.useEffect(() => {
    setModerationPassed(false);
    setModerationResponse('');
  }, [title, text]);

  React.useEffect(() => {
    if (title && title.length > 0) {
      setCustomErrors(prev => ({ ...prev, title: prev.title, text: '', tags: '' }));
      setShowValidation(true);
    } else if (text && text.length > 0) {
      setCustomErrors(prev => ({ ...prev, title: '', text: prev.text, tags: '' }));
      setShowValidation(true);
    } else if (tags && tags.length > 0) {
      setShowValidation(true);
    }
  }, [title, text, tags]);

  React.useEffect(() => {
    if (showValidation) {
      validateContentLength();
    }
  }, [title, text, tags, showValidation, validateContentLength, inputValue]);

  const isFormInvalid = React.useMemo(() => {
    const hasBasicFields = title && text && tags.length > 0;
    const hasLengthRequirements = title.length >= 10 && title.length <= 100 && text.length >= 30;
    const hasNoErrors =
      !titleErr &&
      !textErr &&
      !tagErr &&
      !customErrors.title &&
      !customErrors.text &&
      !customErrors.tags;
    return !hasBasicFields || !hasLengthRequirements || !hasNoErrors;
  }, [title, text, tags, titleErr, textErr, tagErr, customErrors]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button variant='default' className='bg-primary hover:bg-primary/90 gap-2' size='sm'>
            <PlusCircle size={16} />
            Ask a Question
          </Button>
        </DialogTrigger>
        <DialogContent className='sm:max-w-[600px] max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Ask a New Question</DialogTitle>
            <DialogDescription>
              Fill out the form below to ask your question. Be clear and specific to get the best
              answers.
            </DialogDescription>
          </DialogHeader>

          <div className='grid gap-4 py-4'>
            <div className='grid gap-2'>
              <label htmlFor='title' className='text-sm font-medium'>
                Question Title <span className='text-red-500'>*</span>
              </label>
              <Input
                id='title'
                placeholder='What is your question?'
                className={
                  showValidation && (titleErr || customErrors.title) ? 'border-red-500' : ''
                }
                value={title}
                onChange={e => setTitle(e.target.value)}
              />
              {showValidation && titleErr && <p className='text-sm text-red-500'>{titleErr}</p>}
              {showValidation && !titleErr && customErrors.title && (
                <p className='text-sm text-red-500'>{customErrors.title}</p>
              )}
              <p className='text-xs text-muted-foreground'>Title must be 10-100 characters</p>
            </div>

            <div className='grid gap-2'>
              <label htmlFor='text' className='text-sm font-medium'>
                Question Details <span className='text-red-500'>*</span>
              </label>
              <Textarea
                id='text'
                placeholder='Provide details about your question...'
                className={showValidation && (textErr || customErrors.text) ? 'border-red-500' : ''}
                rows={5}
                value={text}
                onChange={e => setText(e.target.value)}
              />
              {showValidation && textErr && <p className='text-sm text-red-500'>{textErr}</p>}
              {showValidation && !textErr && customErrors.text && (
                <p className='text-sm text-red-500'>{customErrors.text}</p>
              )}
              <p className='text-xs text-muted-foreground'>
                Question details must be at least 30 characters
              </p>
            </div>

            <div className='grid gap-2'>
              <label htmlFor='tags' className='text-sm font-medium'>
                Tags <span className='text-red-500'>*</span>
              </label>
              <p className='text-xs text-muted-foreground'>
                Add up to 5 tags to categorize your question
              </p>

              <div
                className={`flex flex-wrap items-center gap-2 p-2 border rounded-md bg-white min-h-[38px] ${showValidation && (tagErr || customErrors.tags) && hasInteractedWithTags ? 'border-red-500' : ''}`}>
                <TagsDisplay tags={tags} onTagClick={removeTag} className='flex-wrap gap-2' />

                <Input
                  id='tags'
                  placeholder={
                    tags.length === 0
                      ? 'Type and press Enter to add a tag. Click on a tag to delete it'
                      : ''
                  }
                  className='flex-grow border-0 focus-visible:ring-0 focus-visible:ring-offset-0 pl-0'
                  value={inputValue}
                  onChange={handleTagInput}
                  onKeyDown={handleTagKeyDown}
                />
              </div>

              {showValidation && tagErr && hasInteractedWithTags && (
                <p className='text-sm text-red-500'>{tagErr}</p>
              )}
              {showValidation && !tagErr && customErrors.tags && hasInteractedWithTags && (
                <p className='text-sm text-red-500'>{customErrors.tags}</p>
              )}
            </div>
          </div>

          <Alert className='bg-amber-50 border-amber-200 text-amber-800'>
            <AlertDescription className='text-xs'>
              * indicates required fields. Your question will be posted under your account name.
              Your question will be automatically moderated when you submit.
            </AlertDescription>
          </Alert>

          {similarQuestion && (
            <div className='mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md'>
              <div className='flex justify-between items-center mb-2'>
                <h3 className='text-sm font-medium text-amber-800'>Similar Question Found</h3>
                {isCheckingSimilarity ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <span className='text-sm text-amber-800'>
                    Similarity: {(similarityScore * 100).toFixed(1)}%
                  </span>
                )}
              </div>
              <div className='prose prose-sm max-w-none'>
                <ReactMarkdown>
                  {`**Title:** ${similarQuestion.title}\n\n**Details:** ${similarQuestion.text.substring(0, 150)}${similarQuestion.text.length > 150 ? '...' : ''}`}
                </ReactMarkdown>
              </div>
              <div className='mt-2 text-xs text-amber-700'>
                Consider checking this question before posting to avoid duplicates.
              </div>
              <div className='mt-2 flex justify-end'>
                <Button
                  variant='outline'
                  size='sm'
                  className='text-amber-800 border-amber-300 hover:bg-amber-100'
                  onClick={() => {
                    setOpen(false);
                    navigate(`/question/${similarQuestion._id}`);
                  }}>
                  View Similar Question
                </Button>
              </div>
            </div>
          )}

          {isModerating && (
            <div className='mt-4'>
              <div className='flex items-center justify-center'>
                <Loader2 className='h-4 w-4 animate-spin mr-2' />
                <span className='text-sm'>
                  {isCheckingSimilarity ? 'Checking similar questions...' : 'Moderating content...'}
                </span>
              </div>
            </div>
          )}

          <div className='grid gap-2 border-t pt-4 mt-2'>
            <div className='flex justify-between items-center'>
              <label className='text-sm font-medium'>AI Solution Preview</label>
              <div className='flex gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handleGenerateAISolution}
                  disabled={isGeneratingSolution || !text}>
                  {isGeneratingSolution ? (
                    <>
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      Generating...
                    </>
                  ) : (
                    'Generate AI Solution'
                  )}
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setFollowUpOpen(true)}
                  disabled={!aiSolution}>
                  Follow Up
                </Button>
              </div>
            </div>
            {solutionError && <p className='text-sm text-red-500'>{solutionError}</p>}
            {(aiSolution || followUpResponse) && (
              <div className='p-4 bg-slate-50 rounded-md max-h-[200px] overflow-y-auto'>
                <div className='prose prose-sm max-w-none'>
                  <ReactMarkdown>
                    {`${aiSolution || ''}${
                      followUpResponse
                        ? `\n\n----\n\n**Follow-up Response:**\n\n${followUpResponse || ''}`
                        : ''
                    }`}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className='mt-4'>
            <Button variant='outline' onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleModerateAndSubmit}
              className='bg-primary'
              disabled={isFormInvalid || isSubmitting || isModerating}>
              {isSubmitting || isModerating ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  {isModerating ? 'Moderating...' : 'Posting...'}
                </>
              ) : (
                'Post Question'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={followUpOpen} onOpenChange={setFollowUpOpen}>
        <DialogContent className='sm:max-w-[550px]'>
          <DialogHeader>
            <DialogTitle>Ask a Follow-up Question</DialogTitle>
          </DialogHeader>
          <div className='py-4'>
            <Textarea
              placeholder='Type your follow-up question here...'
              value={followUpQuestion}
              onChange={e => setFollowUpQuestion(e.target.value)}
              className='min-h-[100px]'
            />
          </div>
          <div className='flex justify-end gap-2'>
            <Button variant='outline' size='sm' onClick={() => setFollowUpOpen(false)}>
              Cancel
            </Button>
            <Button
              size='sm'
              onClick={handleFollowUp}
              disabled={isGeneratingFollowUp || !followUpQuestion.trim()}>
              {isGeneratingFollowUp ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Generating...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Moderation Response Dialog */}
      <Dialog
        open={showModerationDialog}
        onOpenChange={newValue => {
          setShowModerationDialog(newValue);
        }}
        modal={true}>
        <DialogContent className='sm:max-w-[500px]'>
          {/* Add close button that only works if moderation failed */}
          {!moderationPassed && (
            <button
              className='absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground'
              onClick={() => setShowModerationDialog(false)}>
              <X className='h-4 w-4' />
              <span className='sr-only'>Close</span>
            </button>
          )}

          <DialogHeader>
            <DialogTitle>
              {moderationPassed ? (
                <span className='text-green-600'>Moderation Passed</span>
              ) : (
                <span className='text-red-600'>Moderation Failed</span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className='py-4'>
            <div className='p-4 bg-slate-50 rounded-md max-h-[300px] overflow-y-auto'>
              <div className='prose prose-sm max-w-none'>
                {!moderationResponse ? (
                  <p className='text-gray-500'>No moderation details available.</p>
                ) : (
                  <ReactMarkdown>{moderationResponse}</ReactMarkdown>
                )}
              </div>
              {similarQuestion && moderationPassed && (
                <div className='mt-4 text-sm text-amber-700'>
                  <p>
                    Note: A similar question was found with {(similarityScore * 100).toFixed(1)}%
                    similarity.
                  </p>
                  <p>You may want to check that question before continuing.</p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            {!moderationPassed && (
              <Button onClick={() => setShowModerationDialog(false)} variant='default'>
                Edit Question
              </Button>
            )}
            {moderationPassed && (
              <div className='text-green-600 text-sm'>
                Your question has been posted successfully!
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AskQuestionButton;
